import '../styles/GlobalDesign.css';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api'
  : process.env.REACT_APP_API_URL || '/api';

const AdminWhatsApp = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, expired
  const [selectedTemplate, setSelectedTemplate] = useState('reminder');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [customMessages, setCustomMessages] = useState({});
  const [isEditingMessage, setIsEditingMessage] = useState(false);

  // Templates de messages
  const messageTemplates = {
    reminder: {
      name: '🔔 Rappel d\'expiration',
      message: (user) => {
        let expirationText = '';
        if (user.subscription_end) {
          const expirationDate = new Date(user.subscription_end);
          const formattedDate = expirationDate.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
          });
          expirationText = `\n\n📅 Date d'expiration: ${formattedDate}`;
        }
        return `Bonjour ${user.nom},\n\nVotre abonnement Fluxivo expire bientôt !${expirationText}\n\n📺 Pour continuer à profiter de toutes vos chaînes préférées, renouvelez dès maintenant.\n\n✨ Accédez à notre page: http://51.254.135.114/plans\n\nMerci de votre confiance ! 🙏`;
      }
    },
    welcome: {
      name: '👋 Message de bienvenue',
      message: (user) => `Bonjour ${user.nom},\n\nBienvenue sur Fluxivo ! 🎉\n\nVotre abonnement est maintenant actif.\n\n📱 Vos identifiants ont été envoyés par email.\n\nBesoin d'aide ? Répondez à ce message.\n\nBon visionnage ! 📺`
    },
    support: {
      name: '💬 Message de support',
      message: (user) => `Bonjour ${user.nom},\n\nNous avons remarqué que vous pourriez avoir besoin d'aide.\n\n💡 Notre équipe est là pour vous assister.\n\nRépondez à ce message pour toute question.\n\nÀ votre service ! 🙏`
    },
    promo: {
      name: '🎁 Promotion spéciale',
      message: (user) => `Bonjour ${user.nom},\n\n🎁 OFFRE SPÉCIALE pour vous !\n\n✨ Renouvelez maintenant et bénéficiez de -20% sur votre prochain abonnement.\n\nCode promo: RENOUVEAU20\n\n👉 http://51.254.135.114/plans\n\nOffre limitée ! ⏰`
    },
    credentials: {
      name: '🔑 Renvoyer identifiants',
      message: (user) => `Bonjour ${user.nom},\n\nVoici vos identifiants Fluxivo:\n\n🌐 Serveur: http://51.254.135.114\n👤 Username: [Voir email]\n🔑 Password: [Voir email]\n\n📧 Consultez votre email pour les détails complets.\n\nBon visionnage ! 📺`
    },
    payment_failed: {
      name: '❌ Paiement échoué',
      message: (user) => `Bonjour ${user.nom},\n\nNous avons remarqué que votre dernier paiement n'a pas abouti. 😔\n\n🔄 Pas de souci ! Vous pouvez réessayer facilement :\n\n👉 http://51.254.135.114/plans\n\n📞 Besoin d'aide ? Contactez-nous directement.\n\nNous sommes là pour vous ! 🙏`
    }
  };

  // Initialiser les messages personnalisés avec les templates par défaut
  useEffect(() => {
    const initialMessages = {};
    Object.keys(messageTemplates).forEach(key => {
      initialMessages[key] = messageTemplates[key].message({ nom: '[Nom]' });
    });
    setCustomMessages(initialMessages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    filterUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterStatus, users]);

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/users-whatsapp`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement utilisateurs:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/admin/login');
      }
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    // Filtrer par statut
    if (filterStatus === 'active') {
      filtered = filtered.filter(u => u.subscription_status === 'active');
    } else if (filterStatus === 'expired') {
      filtered = filtered.filter(u => u.subscription_status === 'expired');
    }

    // Filtrer par recherche
    if (searchTerm) {
      filtered = filtered.filter(u => 
        u.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.whatsapp?.includes(searchTerm)
      );
    }

    setFilteredUsers(filtered);
  };

  const sendWhatsAppMessage = (user) => {
    if (!user.whatsapp) {
      alert('Aucun numéro WhatsApp pour cet utilisateur');
      return;
    }

    const messageText = customMessages[selectedTemplate] || messageTemplates[selectedTemplate].message(user);
    const personalizedMessage = messageText.replace(/\[Nom\]/g, user.nom);
    const whatsappNumber = user.whatsapp.replace(/[^0-9]/g, '');
    const encodedMessage = encodeURIComponent(personalizedMessage);
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
  };

  const sendBulkWhatsApp = () => {
    if (selectedUsers.length === 0) {
      alert('Veuillez sélectionner au moins un utilisateur');
      return;
    }

    selectedUsers.forEach((userId, index) => {
      const user = users.find(u => u.id === userId);
      if (user && user.whatsapp) {
        setTimeout(() => {
          sendWhatsAppMessage(user);
        }, index * 2000); // Délai de 2 secondes entre chaque message
      }
    });

    setSelectedUsers([]);
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.id));
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'active':
        return <span className="status-badge active">✓ Actif</span>;
      case 'expired':
        return <span className="status-badge expired">✕ Expiré</span>;
      default:
        return <span className="status-badge inactive">- Sans abonnement</span>;
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="loading">Chargement...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-content whatsapp-page">
        <div className="page-header">
          <h1>📱 Relance WhatsApp</h1>
          <p>Envoyez des messages WhatsApp à vos utilisateurs</p>
        </div>

        {/* Sélection du template */}
        <div className="template-selector">
          <label>Template de message:</label>
          <select 
            value={selectedTemplate} 
            onChange={(e) => setSelectedTemplate(e.target.value)}
            className="template-select"
          >
            {Object.entries(messageTemplates).map(([key, template]) => (
              <option key={key} value={key}>{template.name}</option>
            ))}
          </select>
          
          <div className="template-preview">
            <div className="preview-header">
              <strong>📝 Aperçu du message:</strong>
              <button 
                className="edit-message-btn"
                onClick={() => setIsEditingMessage(!isEditingMessage)}
                type="button"
              >
                {isEditingMessage ? '✔️ Valider' : '✏️ Modifier'}
              </button>
            </div>
            {isEditingMessage ? (
              <textarea
                className="message-editor"
                value={customMessages[selectedTemplate] || ''}
                onChange={(e) => setCustomMessages({
                  ...customMessages,
                  [selectedTemplate]: e.target.value
                })}
                rows={10}
                placeholder="Rédigez votre message personnalisé...\n\nUtilisez [Nom] pour insérer le nom du client."
              />
            ) : (
              <pre>{customMessages[selectedTemplate] || messageTemplates[selectedTemplate].message({ nom: '[Nom]' })}</pre>
            )}
          </div>
        </div>

        {/* Filtres et recherche */}
        <div className="filters-section">
          <input
            type="text"
            placeholder="🔍 Rechercher (nom, email, WhatsApp)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />

          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">Tous les statuts</option>
            <option value="active">Actifs uniquement</option>
            <option value="expired">Expirés uniquement</option>
          </select>

          {selectedUsers.length > 0 && (
            <button onClick={sendBulkWhatsApp} className="bulk-send-btn">
              📤 Envoyer à {selectedUsers.length} utilisateur(s)
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-value">{users.length}</div>
            <div className="stat-label">Total utilisateurs</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{users.filter(u => u.whatsapp).length}</div>
            <div className="stat-label">Avec WhatsApp</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{filteredUsers.length}</div>
            <div className="stat-label">Affichés</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{selectedUsers.length}</div>
            <div className="stat-label">Sélectionnés</div>
          </div>
        </div>

        {/* Table des utilisateurs */}
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Nom</th>
                <th>Email</th>
                <th>WhatsApp</th>
                <th>Statut</th>
                <th>Expiration</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => toggleUserSelection(user.id)}
                      disabled={!user.whatsapp}
                    />
                  </td>
                  <td>{user.nom}</td>
                  <td>{user.email}</td>
                  <td>
                    {user.whatsapp ? (
                      <span className="whatsapp-number">✓ {user.whatsapp}</span>
                    ) : (
                      <span className="no-whatsapp">✕ Non renseigné</span>
                    )}
                  </td>
                  <td>{getStatusBadge(user.subscription_status)}</td>
                  <td>
                    {user.subscription_end ? (
                      new Date(user.subscription_end).toLocaleDateString('fr-FR')
                    ) : '-'}
                  </td>
                  <td>
                    <button
                      onClick={() => sendWhatsAppMessage(user)}
                      disabled={!user.whatsapp}
                      className="send-btn"
                      title="Envoyer message WhatsApp"
                    >
                      💬 Envoyer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredUsers.length === 0 && (
            <div className="no-results">
              Aucun utilisateur trouvé
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminWhatsApp;
